import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Timestamp } from "firebase/firestore";
import { apiCall } from "../config/api";
import dayjs from "dayjs";

const formatDateTime = (ts) => {
  if (!ts) return "-";
  let date;

  if (ts instanceof Timestamp) {
    date = ts.toDate();
  } else if (typeof ts?.toDate === "function") {
    date = ts.toDate();
  } else if (ts instanceof Date) {
    date = ts;
  } else if (typeof ts === "string") {
    // ISO string support
    date = new Date(ts);
    if (isNaN(date.getTime())) return "Geçersiz Tarih";
  } else {
    return "-";
  }

  return new Intl.DateTimeFormat("tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const typeChip = (type) => {
  const map = {
    "topic-broadcast": { label: "Topic", color: "primary" },
    "send-to-all": { label: "Herkese", color: "success" },
  };
  const conf = map[type] || { label: type || "Bilinmiyor", color: "default" };
  return <Chip size="small" label={conf.label} color={conf.color} />;
};

const NotificationLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const allSelected =
    selectedIds.length > 0 && selectedIds.length === logs.length;
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const opts = {};
      if (dateStart) opts.start = new Date(dateStart).toISOString();
      if (dateEnd) {
        // include end day's 23:59:59
        const end = dayjs(dateEnd).endOf("day").toDate().toISOString();
        opts.end = end;
      }
      if (typeFilter !== "all") opts.type = typeFilter;

      const response = await apiCall("/api/announcements/logs", "GET", null, {
        limit: 200,
        ...opts,
      });

      console.log("Announcement logs response:", response); // Debug log

      // Safely handle response
      if (response && response.data && Array.isArray(response.data.logs)) {
        setLogs(response.data.logs);
      } else if (response && Array.isArray(response.logs)) {
        setLogs(response.logs);
      } else {
        console.warn("No logs in response, using empty array");
        setLogs([]);
      }
    } catch (err) {
      console.error("announcement_logs read error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    load();
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      if (filterType !== "all" && l.type !== filterType) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${l.title || ""} ${l.body || ""} ${l.topic || ""} ${
          l.segment || ""
        }`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [logs, filterType, search]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.warn("copy failed", e);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        📘 Bildirim Logları
      </Typography>

      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Tür</InputLabel>
            <Select
              value={filterType}
              label="Tür"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="topic-broadcast">Topic</MenuItem>
              <MenuItem value="send-to-all">Herkese</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Ara (başlık/içerik)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <TextField
            size="small"
            type="date"
            label="Başlangıç"
            InputLabelProps={{ shrink: true }}
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="Bitiş"
            InputLabelProps={{ shrink: true }}
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sunucu Tür</InputLabel>
            <Select
              value={typeFilter}
              label="Sunucu Tür"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">Hepsi</MenuItem>
              <MenuItem value="single">Tekli</MenuItem>
              <MenuItem value="bulk">Toplu</MenuItem>
              <MenuItem value="send-to-all">Herkese</MenuItem>
              <MenuItem value="topic-broadcast">Topic</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Yenile">
            <span>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              // Build CSV
              const headers = [
                "id",
                "createdAt",
                "type",
                "topic",
                "segment",
                "title",
                "body",
                "success",
                "failure",
              ];
              const rows = filtered.map((r) => {
                const totals = r.totals || {};
                return [
                  r.id,
                  typeof r.createdAt === "string"
                    ? r.createdAt
                    : formatDateTime(r.createdAt),
                  r.type || "",
                  r.topic || "",
                  r.segment || "",
                  (r.title || "").replaceAll("\n", " ").replaceAll('"', '""'),
                  (r.body || "").replaceAll("\n", " ").replaceAll('"', '""'),
                  totals.success ?? "",
                  totals.failure ?? "",
                ];
              });
              const csv = [headers, ...rows]
                .map((arr) => arr.map((v) => `"${String(v ?? "")}"`).join(","))
                .join("\n");
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `announcement_logs_${Date.now()}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            CSV Dışa Aktar
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} elevation={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1,
          }}
        >
          <Typography variant="subtitle2">Toplam: {filtered.length}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={selectedIds.length === 0 || loading}
              onClick={async () => {
                try {
                  const response = await apiCall(
                    "/api/announcements/logs",
                    "DELETE",
                    { ids: selectedIds }
                  );

                  if (response.success) {
                    setSelectedIds([]);
                    await load();
                  } else {
                    console.error("Log silme hatası:", response.error);
                  }
                } catch (error) {
                  console.error("Log silme sırasında bir hata oluştu:", error);
                }
              }}
            >
              Seçili Olanları Sil ({selectedIds.length})
            </Button>
          </Box>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(filtered.map((f) => f.id));
                    } else {
                      setSelectedIds([]);
                    }
                  }}
                />
              </TableCell>
              <TableCell>Tarih/Saat</TableCell>
              <TableCell>Tür</TableCell>
              <TableCell>Konu/Segment</TableCell>
              <TableCell>Başlık</TableCell>
              <TableCell>İçerik</TableCell>
              <TableCell align="right">Başarılı</TableCell>
              <TableCell align="right">Hatalı</TableCell>
              <TableCell align="center">Detay</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row) => {
              const totals = row.totals || {};
              const success =
                typeof totals.success === "number"
                  ? totals.success
                  : row.type === "topic-broadcast"
                  ? null
                  : undefined;
              const failure =
                typeof totals.failure === "number"
                  ? totals.failure
                  : row.type === "topic-broadcast"
                  ? null
                  : undefined;
              const topicOrSegment =
                row.type === "topic-broadcast"
                  ? row.topic || "-"
                  : row.segment || "—";
              return (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds((prev) =>
                            Array.from(new Set([...prev, row.id]))
                          );
                        } else {
                          setSelectedIds((prev) =>
                            prev.filter((id) => id !== row.id)
                          );
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                  <TableCell>{typeChip(row.type)}</TableCell>
                  <TableCell>{topicOrSegment}</TableCell>
                  <TableCell>{row.title || "-"}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        maxWidth={260}
                        title={row.body || ""}
                      >
                        {row.body || "-"}
                      </Typography>
                      {row.body && (
                        <Tooltip title="İçeriği kopyala">
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(row.body)}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {success === null ? "topic" : success ?? "-"}
                  </TableCell>
                  <TableCell align="right">
                    {failure === null ? "topic" : failure ?? "-"}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Detayları gör">
                      <IconButton
                        onClick={() => {
                          setDetailItem(row);
                          setDetailOpen(true);
                        }}
                      >
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {loading ? "Yükleniyor…" : "Kayıt bulunamadı"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bildirim Detayı</DialogTitle>
        <DialogContent dividers>
          {detailItem && (
            <pre style={{ whiteSpace: "pre-wrap" }}>
              {JSON.stringify(
                detailItem,
                (key, value) => {
                  if (value instanceof Timestamp)
                    return value.toDate().toISOString();
                  return value;
                },
                2
              )}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NotificationLogsPage;
